import {
  BooleanInput,
  CheckboxGroupInput,
  Create,
  CreateContextProvider,
  DateTimeInput,
  Form,
  required,
  SaveButton,
  SelectInput,
  TextInput,
  NumberInput,
  Toolbar,
  TopToolbar,
  useCreateController,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Card,
  Divider,
  Stack,
  Typography,
  Button as MUIButton,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Resources } from '../resource';

import CustomTagInput from 'src/pages/admin/components/CustomTagInput';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import useUnlimitedAgents from './useUnlimitedAgents';
import { chains } from 'src/assets/data';

interface FormProps {
  isCreate: boolean;
}
const UnlimitedAgentsForm = (props: FormProps) => {
  const { isCreate } = props;
  const t = useTranslate();
  const redirect = useRedirect();
  const { watch, setValue, trigger } = useFormContext();
  const record = useRecordContext();
  const { findUnlimitedAgents } = useUnlimitedAgents();
  const [addressMessage, setAddressMessage] = useState('');
  const id = useWatch({ name: 'id' });
  const [noCreate, setNoCreate] = useState<boolean>(true);

  const loadForEdit = useCallback(async () => {
    setAddressMessage('');
    const curId = (id ?? '').trim();
    if (!curId) return;

    try {
      const agent = await findUnlimitedAgents(curId);
      if (!agent) return;
      setNoCreate(true);
      setValue('userId', agent.userId);
      setValue('commissionRateN', agent.commissionRateN);
      setValue('status', agent.status);
    } catch {
      setAddressMessage('module.unlimitedAgents.validate.required');
    }
  }, [id, setValue, findUnlimitedAgents]);

  const hasTypedRef = useRef(false);

  useEffect(() => {
    if (isCreate) return;
    loadForEdit().catch(() => {}); 
  }, [isCreate, loadForEdit]);

  return (
    <>
      <Stack spacing={3} gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.userId')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="userId"
              validate={[required()]}
              placeholder={t('module.unlimitedAgents.userId')}
              InputProps={{ readOnly: !isCreate }}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="string"
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.commissionRateN')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="commissionRateN"
              validate={[required()]}
              placeholder={t('module.unlimitedAgents.commissionRateN')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="number"
            />
          </Box>
        </Stack>

        <Stack direction={'row'} alignItems={'center'} spacing={2} mb={1}>
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.status')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <BooleanInput
              label=""
              source="status"
              helperText={<span style={{ color: 'red' }}>{''}</span>}
              format={(v) => v === 'ACTIVE' || v === true}
              parse={(v) => (v ? 'ACTIVE' : 'INACTIVE')}
            />
          </Box>
        </Stack>
        {/* <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.contractVolumeThreshold')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="contractVolumeThreshold"
              placeholder={t('module.unlimitedAgents.contractVolumeThreshold')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="number"
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.extendedCommissionRate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="extendedCommissionRate"
              placeholder={t('module.unlimitedAgents.extendedCommissionRate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="number"
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.indirectCommissionRate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="indirectCommissionRate"
              placeholder={t('module.unlimitedAgents.indirectCommissionRate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="number"
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.makerFeeRate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="makerFeeRate"
              placeholder={t('module.unlimitedAgents.makerFeeRate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="number"
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.memeFeeRate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="memeFeeRate"
              placeholder={t('module.unlimitedAgents.memeFeeRate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="number"
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.memeFeeRebate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="memeFeeRebate"
              placeholder={t('module.unlimitedAgents.memeFeeRebate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="number"
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.memeVolumeThreshold')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="memeVolumeThreshold"
              placeholder={t('module.unlimitedAgents.memeVolumeThreshold')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="number"
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.takerFeeRate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="takerFeeRate"
              placeholder={t('module.unlimitedAgents.takerFeeRate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="number"
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.totalCommission')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="totalCommission"
              placeholder={t('module.unlimitedAgents.totalCommission')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="number"
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.unlimitedAgents.totalVolumeUsd')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="totalVolumeUsd"
              placeholder={t('module.unlimitedAgents.totalVolumeUsd')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="number"
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
          mt={2}
        >
          <Box sx={{ flexBasis: '25%', flexShrink: 0, mt: -1 }}>
            <Typography variant="body2"> {t('module.unlimitedAgents.userCount')}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              source="userCount"
              label=""
              placeholder={t('module.unlimitedAgents.userCount')}
              fullWidth
              helperText={<span style={{ color: 'red' }}>{addressMessage}</span>}
              onKeyDown={(e) => {
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }}
              onChange={() => {
                hasTypedRef.current = true;
              }}
              onBlur={(e) => {
                if (isCreate && hasTypedRef.current) {
                  handleOnEdit();
                  hasTypedRef.current = false;
                }
              }}
            />
          </Box>
        </Stack> */}
      </Stack>
    </>
  );
};

export default UnlimitedAgentsForm;
